'use client';
/**
 * Примитивы дизайн-системы LOGIXA.
 *
 * Без бизнес-логики и без обращений к API — только форма и состояние.
 * Цвета берутся из семантических токенов (src/styles/tokens.css), поэтому
 * тёмная тема не требует здесь ни одной ветки.
 *
 * Оранжевый живёт ровно в двух местах: `variant="primary"` у кнопки и
 * активный пункт навигации. Всё остальное нейтральное — так действие
 * читается с одного взгляда.
 */
import { forwardRef, useEffect } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, SelectHTMLAttributes, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

export const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

/* ── Button ─────────────────────────────────────────────────────── */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

const BTN_BASE =
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-medium ' +
  'rounded-[var(--radius-sm)] border transition-colors duration-150 ' +
  'focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)] ' +
  'disabled:opacity-45 disabled:pointer-events-none';

const BTN_VARIANT: Record<Variant, string> = {
  primary:   'bg-[var(--brand)] border-[var(--brand)] text-[var(--on-brand)] hover:bg-[var(--brand-hover)] hover:border-[var(--brand-hover)]',
  secondary: 'bg-[var(--surface)] border-[var(--border-strong)] text-[var(--text-primary)] hover:bg-[var(--surface-hover)]',
  ghost:     'bg-transparent border-transparent text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]',
  danger:    'bg-transparent border-[var(--danger)] text-[var(--danger)] hover:bg-[var(--danger-subtle)]',
};

const BTN_SIZE: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-[12px]',
  md: 'h-9 px-3.5 text-[13px]',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: LucideIcon;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon: Icon, className, children, ...rest }, ref,
) {
  return (
    <button ref={ref} className={cx(BTN_BASE, BTN_VARIANT[variant], BTN_SIZE[size], className)} {...rest}>
      {Icon && <Icon size={size === 'sm' ? 13 : 15} strokeWidth={2} />}
      {children}
    </button>
  );
});

/** Кнопка-иконка: квадрат, доступное имя обязательно. */
export function IconButton({
  icon: Icon, label, size = 'md', variant = 'ghost', className, ...rest
}: { icon: LucideIcon; label: string; size?: Size; variant?: Variant } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      aria-label={label} title={label}
      className={cx(BTN_BASE, BTN_VARIANT[variant], size === 'sm' ? 'h-7 w-7' : 'h-9 w-9', className)}
      {...rest}
    >
      <Icon size={size === 'sm' ? 14 : 16} strokeWidth={2} />
    </button>
  );
}

/* ── Поля ввода ─────────────────────────────────────────────────── */

const FIELD =
  'w-full bg-[var(--surface-sunken)] border border-[var(--border-strong)] rounded-[var(--radius-sm)] ' +
  'text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] ' +
  'focus:outline-none focus:border-[var(--brand)] focus:shadow-[var(--focus-ring)] transition-colors';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cx(FIELD, 'h-9 px-2.5', className)} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...rest }, ref) {
    return (
      <select ref={ref} className={cx(FIELD, 'h-9 pl-2.5 pr-7 cursor-pointer appearance-none', className)} {...rest}>
        {children}
      </select>
    );
  },
);

/* ── Статусы ────────────────────────────────────────────────────── */

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'category';

const TONE_BG: Record<Tone, string> = {
  neutral:  'bg-[var(--surface-sunken)] text-[var(--text-secondary)] border-[var(--border)]',
  success:  'bg-[var(--success-subtle)] text-[var(--success)] border-[color-mix(in_srgb,var(--success)_30%,transparent)]',
  warning:  'bg-[var(--warning-subtle)] text-[var(--warning)] border-[color-mix(in_srgb,var(--warning)_30%,transparent)]',
  danger:   'bg-[var(--danger-subtle)] text-[var(--danger)] border-[color-mix(in_srgb,var(--danger)_30%,transparent)]',
  info:     'bg-[var(--info-subtle)] text-[var(--info)] border-[color-mix(in_srgb,var(--info)_30%,transparent)]',
  brand:    'bg-[var(--brand-subtle)] text-[var(--brand)] border-[var(--brand-border)]',
  category: 'bg-[var(--category-subtle)] text-[var(--category)] border-[color-mix(in_srgb,var(--category)_30%,transparent)]',
};

const TONE_DOT: Record<Tone, string> = {
  neutral: 'bg-[var(--text-muted)]', success: 'bg-[var(--success)]', warning: 'bg-[var(--warning)]',
  danger: 'bg-[var(--danger)]', info: 'bg-[var(--info)]', brand: 'bg-[var(--brand)]', category: 'bg-[var(--category)]',
};

/** Статус кодируется формой И цветом: точка + подпись, не только заливка. */
export function Badge({ tone = 'neutral', dot = false, children, className, title }: {
  tone?: Tone; dot?: boolean; children: ReactNode; className?: string; title?: string;
}) {
  return (
    <span title={title} className={cx(
      'inline-flex items-center gap-1.5 h-[22px] px-2 rounded-[var(--radius-xs)] border',
      'text-[11px] font-medium leading-none whitespace-nowrap', TONE_BG[tone], className,
    )}>
      {dot && <i className={cx('w-1.5 h-1.5 rounded-full shrink-0', TONE_DOT[tone])} />}
      {children}
    </span>
  );
}

export function StatusDot({ tone = 'neutral', className, title }: { tone?: Tone; className?: string; title?: string }) {
  return <i title={title} className={cx('inline-block w-2 h-2 rounded-full shrink-0', TONE_DOT[tone], className)} />;
}

/* ── Контейнеры ─────────────────────────────────────────────────── */

/** Панель: рамка вместо тени — тень остаётся привилегией поповеров. */
export function Panel({ className, style, children }: { className?: string; style?: React.CSSProperties; children: ReactNode }) {
  return (
    <section style={style} className={cx('bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius-md)]', className)}>
      {children}
    </section>
  );
}

export function PanelHeader({ title, action, meta }: { title: ReactNode; action?: ReactNode; meta?: ReactNode }) {
  return (
    <header className="flex items-center gap-3 h-[52px] px-4 border-b border-[var(--border)]">
      <h2 className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)] truncate">{title}</h2>
      {meta && <span className="text-[12.5px] text-[var(--text-muted)] tabular-nums">{meta}</span>}
      {action && <div className="ml-auto flex items-center gap-1.5">{action}</div>}
    </header>
  );
}

export function Spinner({ size = 14, className }: { size?: number; className?: string }) {
  return (
    <span
      role="status" aria-label="Loading"
      className={cx('inline-block rounded-full border-2 border-current border-r-transparent animate-spin', className)}
      style={{ width: size, height: size, opacity: 0.55 }}
    />
  );
}

export function Avatar({ name, size = 28 }: { name: string; size?: number }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');
  return (
    <span
      className="inline-flex items-center justify-center rounded-[var(--radius-sm)] bg-[var(--surface-sunken)] border border-[var(--border)] text-[var(--text-secondary)] font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
      aria-hidden
    >
      {initials || '—'}
    </span>
  );
}

/* ── Drawer ─────────────────────────────────────────────────────────
   Боковая панель для деталей записи. В операционном софте она лучше
   модалки по центру: список слева остаётся виден, можно щёлкать записи
   подряд, не теряя контекст. */
export function Drawer({
  open, onClose, title, subtitle, footer, width = 520, children,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  footer?: ReactNode;
  width?: number;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Фон не прокручивается, пока панель открыта
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex justify-end" role="dialog" aria-modal="true">
      <button className="absolute inset-0 bg-black/40" onClick={onClose} aria-label="Close" tabIndex={-1} />
      <aside
        className="relative h-full flex flex-col bg-[var(--surface)] border-l border-[var(--border-strong)] shadow-[var(--shadow-md)] w-full"
        style={{ maxWidth: width }}
      >
        <header className="flex items-start gap-3 px-5 h-[60px] shrink-0 border-b border-[var(--border)]">
          <div className="min-w-0 flex-1 pt-3">
            <h2 className="text-[16px] font-semibold text-[var(--text-primary)] truncate leading-tight">{title}</h2>
            {subtitle && <p className="text-[12.5px] text-[var(--text-muted)] truncate">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="mt-2.5 w-8 h-8 grid place-items-center rounded-[var(--radius-sm)] text-[var(--text-muted)]
                       hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)] transition-colors
                       focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]"
            aria-label="Close"
          >
            <span aria-hidden>✕</span>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">{children}</div>

        {footer && (
          <footer className="shrink-0 px-5 py-3 border-t border-[var(--border)] flex items-center gap-2">{footer}</footer>
        )}
      </aside>
    </div>
  );
}

/** Подпись + значение в деталях: пара строк, выровненных по сетке. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--text-muted)]">{label}</span>
      <span className="text-[13.5px] text-[var(--text-primary)]">{children}</span>
    </div>
  );
}

/* ── Tabs ───────────────────────────────────────────────────────────
   Переключение разделов внутри экрана или панели. Активная вкладка
   помечена линией снизу и тёмным текстом — оранжевый в подложке вкладок
   не участвует, он остаётся цветом действия. */
export function Tabs<T extends string>({ value, onChange, items }: {
  value: T;
  onChange: (v: T) => void;
  items: { key: T; label: ReactNode; count?: number; icon?: LucideIcon }[];
}) {
  return (
    <div role="tablist" className="flex gap-1 border-b border-[var(--border)] overflow-x-auto">
      {items.map((it) => {
        const on = it.key === value;
        const Icon = it.icon;
        return (
          <button
            key={it.key}
            role="tab" aria-selected={on}
            onClick={() => onChange(it.key)}
            className={cx(
              'inline-flex items-center gap-1.5 h-10 px-3 text-[13px] font-medium whitespace-nowrap',
              'border-b-2 -mb-px transition-colors focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)]',
              on
                ? 'border-[var(--brand)] text-[var(--text-primary)]'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]',
            )}
          >
            {Icon && <Icon size={14} />}
            {it.label}
            {it.count != null && (
              <span className="text-[12px] tabular-nums text-[var(--text-muted)]">{it.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ── Формы ──────────────────────────────────────────────────────────
   Поле = подпись + контрол + подсказка/ошибка. До этого каждый экран
   собирал их вручную из <label className="label"> и logixa-input, и
   отступы с размерами расходились от файла к файлу. */

export function Textarea({ className, ...rest }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cx(
        'w-full bg-[var(--surface-sunken)] border border-[var(--border-strong)] rounded-[var(--radius-sm)]',
        'px-2.5 py-2 text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
        'focus:outline-none focus:border-[var(--brand)] focus:shadow-[var(--focus-ring)] transition-colors',
        'resize-y min-h-[72px]',
        className,
      )}
      {...rest}
    />
  );
}

export function Checkbox({ label, hint, className, ...rest }: {
  label: ReactNode; hint?: string;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className={cx('flex items-start gap-2.5 cursor-pointer', className)}>
      <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[var(--brand)] shrink-0" {...rest} />
      <span className="leading-tight">
        <span className="block text-[13px] text-[var(--text-secondary)]">{label}</span>
        {hint && <span className="block text-[12px] text-[var(--text-muted)]">{hint}</span>}
      </span>
    </label>
  );
}

/** Поле формы: подпись, обязательность, подсказка и ошибка в одном месте. */
export function FormField({ label, required, hint, error, htmlFor, children }: {
  label: ReactNode; required?: boolean; hint?: string; error?: string | null;
  htmlFor?: string; children: ReactNode;
}) {
  // Часть переводов уже несёт «*» внутри строки — вторая звёздочка от нас
  // давала «Supplier * *». Ставим свою, только если её там ещё нет.
  const hasStar = typeof label === 'string' && label.trim().endsWith('*');
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-[12px] font-medium text-[var(--text-secondary)]">
        {label}
        {required && !hasStar && <span className="text-[var(--danger)]" aria-hidden> *</span>}
      </label>
      {children}
      {error
        ? <span className="text-[12px] text-[var(--danger)]">{error}</span>
        : hint ? <span className="text-[12px] text-[var(--text-muted)]">{hint}</span> : null}
    </div>
  );
}

/** Группа полей с заголовком: делит длинную форму на смысловые части. */
export function FormSection({ title, hint, children, columns = 1 }: {
  title?: string; hint?: string; children: ReactNode; columns?: 1 | 2;
}) {
  return (
    <section className="flex flex-col gap-3">
      {title && (
        <header className="flex flex-col gap-0.5">
          <h3 className="text-[13px] font-semibold text-[var(--text-primary)]">{title}</h3>
          {hint && <p className="text-[12px] text-[var(--text-muted)]">{hint}</p>}
        </header>
      )}
      <div className={cx('grid gap-3', columns === 2 ? 'grid-cols-2' : 'grid-cols-1')}>{children}</div>
    </section>
  );
}
